/**
 * Secure Admin Authentication Utility
 * 
 * This module provides secure authentication for admin users with:
 * - Database-based credentials with encrypted passwords
 * - Rate limiting to prevent brute force attacks
 * - Secure session management
 * - Proper privilege escalation protection
 */

import { supabase } from './supabase';

// Simple rate limiting implementation (client-side backup)
interface RateLimitEntry {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

class AdminRateLimiter {
  private attempts = new Map<string, RateLimitEntry>();
  private readonly maxAttempts = 5;
  private readonly lockoutDuration = 15 * 60 * 1000; // 15 minutes
  private readonly resetWindow = 60 * 60 * 1000; // 1 hour

  isBlocked(identifier: string): boolean {
    const entry = this.attempts.get(identifier);
    if (!entry) return false;

    const now = Date.now();
    
    // Check if currently blocked
    if (entry.blockedUntil && now < entry.blockedUntil) {
      return true;
    }

    // Reset attempts if enough time has passed
    if (now - entry.lastAttempt > this.resetWindow) {
      this.attempts.delete(identifier);
      return false;
    }

    return false;
  }

  recordAttempt(identifier: string, success: boolean): void {
    const now = Date.now();
    const entry = this.attempts.get(identifier) || { attempts: 0, lastAttempt: now };

    if (success) {
      // Reset on successful login
      this.attempts.delete(identifier);
      return;
    }

    // Increment failed attempts
    entry.attempts += 1;
    entry.lastAttempt = now;

    // Block if too many attempts
    if (entry.attempts >= this.maxAttempts) {
      entry.blockedUntil = now + this.lockoutDuration;
    }

    this.attempts.set(identifier, entry);
  }

  getRemainingLockoutTime(identifier: string): number {
    const entry = this.attempts.get(identifier);
    if (!entry?.blockedUntil) return 0;

    const remaining = entry.blockedUntil - Date.now();
    return Math.max(0, remaining);
  }

  getAttemptCount(identifier: string): number {
    return this.attempts.get(identifier)?.attempts || 0;
  }
}

// Global rate limiter instance
const rateLimiter = new AdminRateLimiter();

/**
 * Admin User Interface
 */
export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  last_login_at?: string;
}

/**
 * Database-based admin authentication with proper security
 */
export interface AdminAuthResult {
  success: boolean;
  user?: AdminUser;
  error?: string;
  remainingLockoutTime?: number;
  attemptsRemaining?: number;
  isLocked?: boolean;
}

export async function authenticateAdmin(email: string, password: string): Promise<AdminAuthResult> {
  const identifier = email.toLowerCase();
  
  // Check client-side rate limiting first
  if (rateLimiter.isBlocked(identifier)) {
    const lockoutTime = rateLimiter.getRemainingLockoutTime(identifier);
    return {
      success: false,
      error: `Too many failed attempts. Try again in ${Math.ceil(lockoutTime / 60000)} minutes.`,
      remainingLockoutTime: lockoutTime,
      isLocked: true
    };
  }

  try {
    // Call the database function to verify password
    const { data, error } = await supabase.rpc('verify_admin_password', {
      input_email: email,
      input_password: password
    });

    if (error) {
      console.error('Admin authentication error:', error);
      return {
        success: false,
        error: 'Authentication service unavailable'
      };
    }

    // If no data returned, authentication failed
    if (!data || data.length === 0) {
      // Record failed attempt
      rateLimiter.recordAttempt(identifier, false);
      const attempts = rateLimiter.getAttemptCount(identifier);
      const remaining = Math.max(0, 5 - attempts);
      
      return {
        success: false,
        error: 'Invalid email or password',
        attemptsRemaining: remaining
      };
    }

    const adminData = data[0];
    
    // Check if account is locked (from database)
    if (adminData.locked_until && new Date(adminData.locked_until) > new Date()) {
      const lockoutTime = new Date(adminData.locked_until).getTime() - Date.now();
      return {
        success: false,
        error: `Account locked due to too many failed attempts. Try again in ${Math.ceil(lockoutTime / 60000)} minutes.`,
        remainingLockoutTime: lockoutTime,
        isLocked: true
      };
    }

    // Check if account is active
    if (!adminData.is_active) {
      return {
        success: false,
        error: 'Account is deactivated. Please contact system administrator.'
      };
    }

    // Authentication successful
    rateLimiter.recordAttempt(identifier, true);
    
    const user: AdminUser = {
      id: adminData.user_id,
      email: adminData.email,
      full_name: adminData.full_name,
      role: adminData.role,
      is_active: adminData.is_active,
      last_login_at: new Date().toISOString()
    };

    return {
      success: true,
      user
    };

  } catch (error) {
    console.error('Unexpected admin authentication error:', error);
    return {
      success: false,
      error: 'Authentication failed. Please try again.'
    };
  }
}

/**
 * Generate a secure session token
 */
export function generateSessionToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Validate session timeout
 */
export function isSessionValid(loginTime: string): boolean {
  try {
    // Default session timeout: 24 hours
    const sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const loginDate = new Date(loginTime);
    const now = new Date();
    const timeDiff = now.getTime() - loginDate.getTime();
    
    return timeDiff < sessionTimeout;
  } catch (error) {
    console.error('Session validation error:', error);
    return false;
  }
}

/**
 * Secure session storage with encryption-like obfuscation
 */
export const secureStorage = {
  setItem: (key: string, value: string): void => {
    try {
      // Simple obfuscation for demo purposes
      // In production, use proper encryption
      const obfuscated = btoa(value + '|' + Date.now());
      localStorage.setItem(`admin_${key}`, obfuscated);
    } catch (error) {
      console.error('Secure storage setItem error:', error);
    }
  },

  getItem: (key: string): string | null => {
    try {
      const obfuscated = localStorage.getItem(`admin_${key}`);
      if (!obfuscated) return null;
      
      const decoded = atob(obfuscated);
      const [value] = decoded.split('|');
      return value;
    } catch (error) {
      console.error('Secure storage getItem error:', error);
      return null;
    }
  },

  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(`admin_${key}`);
    } catch (error) {
      console.error('Secure storage removeItem error:', error);
    }
  },

  clear: (): void => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('admin_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Secure storage clear error:', error);
    }
  }
};

/**
 * Admin Management Functions
 */

/**
 * Create a new admin user (requires super admin privileges)
 */
export async function createAdminUser(userData: {
  email: string;
  password: string;
  full_name: string;
  role?: 'admin' | 'super_admin';
}): Promise<{ success: boolean; error?: string; user?: AdminUser }> {
  try {
    // First create the password hash
    const { data: hashData, error: hashError } = await supabase.rpc('create_admin_password_hash', {
      password: userData.password
    });

    if (hashError || !hashData) {
      return {
        success: false,
        error: 'Failed to create password hash'
      };
    }

    // Insert the new admin user
    const { data, error } = await supabase
      .from('admin_users')
      .insert({
        email: userData.email,
        password_hash: hashData,
        full_name: userData.full_name,
        role: userData.role || 'admin'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating admin user:', error);
      return {
        success: false,
        error: error.message || 'Failed to create admin user'
      };
    }

    return {
      success: true,
      user: {
        id: data.id,
        email: data.email,
        full_name: data.full_name,
        role: data.role,
        is_active: data.is_active
      }
    };
  } catch (error) {
    console.error('Unexpected error creating admin user:', error);
    return {
      success: false,
      error: 'Failed to create admin user'
    };
  }
}

/**
 * Get all admin users (requires admin privileges)
 */
export async function getAdminUsers(): Promise<AdminUser[]> {
  try {
    const { data, error } = await supabase
      .from('admin_users')
      .select('id, email, full_name, role, is_active, last_login_at, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching admin users:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Unexpected error fetching admin users:', error);
    return [];
  }
}

/**
 * Update admin user (requires admin privileges)
 */
export async function updateAdminUser(
  userId: string, 
  updates: Partial<Pick<AdminUser, 'full_name' | 'role' | 'is_active'>>
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('admin_users')
      .update(updates)
      .eq('id', userId);

    if (error) {
      console.error('Error updating admin user:', error);
      return {
        success: false,
        error: error.message || 'Failed to update admin user'
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error updating admin user:', error);
    return {
      success: false,
      error: 'Failed to update admin user'
    };
  }
}